// ---------------------------------------------
// GREY Specialty Coffee Homepage (Pixel-Perfect)
// ---------------------------------------------
// This file implements the homepage for GREY, a premium specialty coffee brand.
// The design is rebuilt from scratch to match the Figma design with pixel-perfect accuracy.
//
// Key features:
// - Custom header and navbar (not sticky)
// - Responsive, editorial hero slideshow with animated coffee bean indicators
// - Full-viewport background with overlay
// - All layout, spacing, and typography tuned to Figma
// - Mobile hamburger menu and overlay
// - Per-slide customizability for future flexibility
// ---------------------------------------------

import React, { useState, useRef, useEffect, useMemo } from 'react';
import '../styles/HomeStyles/HomePage.css';
import '../styles/ShopsStyles.css';
import '../styles/NewHomepageStyles.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { db } from '../services/firebase';
import { collection, getDocs, query, orderBy, doc, getDoc, where } from 'firebase/firestore';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import analytics from '../services/analytics';


// --- SKELETON COMPONENT ---
const ProductSkeleton = ({ count = 4, className = "" }) => {
  const skeletons = Array.from({ length: count }, (_, index) => (
    <div key={index} className={`product-skeleton ${className}`}>
      <div className="skeleton-image"></div>
      <div className="skeleton-content">
        <div className="skeleton-line skeleton-title"></div>
        <div className="skeleton-line skeleton-subtitle"></div>
        <div className="skeleton-line skeleton-price"></div>
      </div>
    </div>
  ));
  return <>{skeletons}</>;
};

// --- SVG ICONS ---
// These are inline SVGs for Search, Cart, Hamburger, and Close icons, styled to match Figma.
const SearchIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="grey-navbar-svg-icon"
  >
    <circle cx="9" cy="9" r="7" stroke="#222" strokeWidth="2" />
    <line x1="14.5" y1="14.5" x2="19" y2="19" stroke="#222" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CartIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 22 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="grey-navbar-svg-icon"
  >
    <circle cx="8" cy="19" r="1.2" stroke="#222" strokeWidth="1.5" />
    <circle cx="17" cy="19" r="1.2" stroke="#222" strokeWidth="1.5" />
    <path
      d="M2 2H4.5L6.5 14.5H18L20 6.5H6.5"
      stroke="#222"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

const HamburgerIcon = () => (
  <svg
    width="28"
    height="22"
    viewBox="0 0 28 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="grey-navbar-svg-icon"
  >
    <rect y="2" width="28" height="2" rx="1" fill="#222" />
    <rect y="10" width="28" height="2" rx="1" fill="#222" />
    <rect y="18" width="28" height="2" rx="1" fill="#222" />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 28 28"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="grey-navbar-svg-icon"
  >
    <line x1="6" y1="6" x2="22" y2="22" stroke="#222" strokeWidth="2" strokeLinecap="round" />
    <line x1="22" y1="6" x2="6" y2="22" stroke="#222" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Add CartIconSVG for use in the button
const CartIconSVG = ({ size = 24, color = '#222' }) => (
  <svg width={size} height={size} viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="19" r="1.2" stroke={color} strokeWidth="1.5" />
    <circle cx="17" cy="19" r="1.2" stroke={color} strokeWidth="1.5" />
    <path d="M2 2H4.5L6.5 14.5H18L20 6.5H6.5" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
  </svg>
);

const CoffeeBeanIndicator = ({ active, onClick, index, movingBeanIndex }) => {
  let beanClass = 'coffee-bean-indicator';
  if (index === movingBeanIndex) beanClass += ' moving';
  if (active && movingBeanIndex === null) beanClass += ' filled';
  return (
    <img
      src={active || index === movingBeanIndex ? '/images/filled.png' : '/images/notfilled.png'}
      className={beanClass}
      onClick={onClick}
      alt="Coffee Bean"
    />
  );
};

// --- Helper Functions ---
// Helper function to generate Google Maps link
const getGoogleMapsLink = (branch) => {
  // Check if the branch has a specific Google Maps link
  if (branch.googleMapsLink) {
    return branch.googleMapsLink;
  }
  
  // Otherwise, generate one from the address
  if (branch.address) {
    const encodedAddress = encodeURIComponent(branch.address);
    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  }
  
  // Fallback: search for the branch name
  const encodedName = encodeURIComponent(branch.name || 'GREY Coffee Qatar');
  return `https://www.google.com/maps/search/?api=1&query=${encodedName}`;
};

// Handle location click
const handleLocationClick = (branch) => {
  // Track location click
  analytics.trackLocationClick(branch.name);
  
  const mapsLink = getGoogleMapsLink(branch);
  window.open(mapsLink, '_blank', 'noopener,noreferrer');
};

// --- Child Components for Clarity ---
const MobileHomepage = ({ slides, bestSellers, branches, branchesLoading, goToSlide, currentSlide, bestSellersLoading, bestSellersError, newDrops, newDropsLoading, newDropsError, setSelectedBranch, setMobileExpandedBranch, selectedBranch, mobileExpandedBranch, CoffeeBeanIndicator, isMobile }) => {
  const [fading, setFading] = useState(false);
  
  const [slideContent, setSlideContent] = useState(slides[currentSlide]);
  const [featuredCoffeeInView, setFeaturedCoffeeInView] = useState(false);
  const [ourLocationInView, setOurLocationInView] = useState(false);
  const [expandedBranches, setExpandedBranches] = useState(new Set());
  const featuredCoffeeRef = useRef(null);
  const ourLocationRef = useRef(null);
  

  const toggleBranchExpansion = (index) => {
    const newExpanded = new Set(expandedBranches);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedBranches(newExpanded);
  };

  const getFullHours = (branch) => {
    // Use Firebase opening hours if available
    if (branch.openingHours) {
      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      
      return days.map((day, index) => {
        const dayInfo = branch.openingHours[day];
        if (dayInfo?.closed) {
          return { day: dayNames[index], hours: 'Closed' };
        }
        if (dayInfo?.open && dayInfo?.close) {
          const openTime = formatTime(dayInfo.open);
          const closeTime = dayInfo.close === '24:00' ? '12:00 AM (Next Day)' : formatTime(dayInfo.close);
          return { day: dayNames[index], hours: `${openTime} - ${closeTime}` };
        }
        return { day: dayNames[index], hours: 'Contact for hours' };
      });
    }

    // Fallback to hardcoded logic if no Firebase data
    const name = branch.name?.toLowerCase() || '';
    if (name.includes('al wakrah') || name.includes('wakra')) {
      return [
        { day: 'Monday', hours: '5:00 AM - 11:00 PM' },
        { day: 'Tuesday', hours: '5:00 AM - 11:00 PM' },
        { day: 'Wednesday', hours: '5:00 AM - 11:00 PM' },
        { day: 'Thursday', hours: '5:00 AM - 12:00 AM' },
        { day: 'Friday', hours: '7:00 AM - 12:00 AM' },
        { day: 'Saturday', hours: '7:00 AM - 11:00 PM' },
        { day: 'Sunday', hours: '5:00 AM - 11:00 PM' }
      ];
    } else if (name.includes('gewan')) {
      return [
        { day: 'Monday', hours: '9:00 AM - 11:00 PM' },
        { day: 'Tuesday', hours: '9:00 AM - 11:00 PM' },
        { day: 'Wednesday', hours: '9:00 AM - 11:00 PM' },
        { day: 'Thursday', hours: '9:00 AM - 11:00 PM' },
        { day: 'Friday', hours: '9:00 AM - 11:00 PM' },
        { day: 'Saturday', hours: '9:00 AM - 11:00 PM' },
        { day: 'Sunday', hours: '9:00 AM - 11:00 PM' }
      ];
    } else if (name.includes('popup')) {
      return [
        { day: 'Contact', hours: branch.contactInfo || '+974 5000 8852' }
      ];
    }
    return [
      { day: 'Contact', hours: 'Call for hours' }
    ];
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour24 = parseInt(hours);
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes} ${ampm}`;
  };

  useEffect(() => {
    if (slideContent.title !== slides[currentSlide].title) {
        setFading(true);
        setTimeout(() => {
            setSlideContent(slides[currentSlide]);
            setFading(false);
        }, 300); // half of the transition duration
    }
  }, [currentSlide, slides, slideContent]);

  // Scroll-triggered animation for mobile section headers
  useEffect(() => {
    const handleScroll = () => {
      if (featuredCoffeeRef.current) {
        const rect = featuredCoffeeRef.current.getBoundingClientRect();
        const inView = rect.top < window.innerHeight * 0.8 && rect.bottom > window.innerHeight * 0.2;
        setFeaturedCoffeeInView(inView);
      }
      
      if (ourLocationRef.current) {
        const rect = ourLocationRef.current.getBoundingClientRect();
        const inView = rect.top < window.innerHeight * 0.8 && rect.bottom > window.innerHeight * 0.2;
        setOurLocationInView(inView);
      }

    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial state
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
    
  return (
    <div className="mobile-homepage">
      {/* --- Hero Section --- */}
      <section className="mobile-hero">
        <div className={`mobile-hero-content ${fading ? 'fading' : ''}`}>
            <img
              src={slideContent.image}
              alt={slideContent.title}
              className="mobile-hero-image"
            />
            <div className="mobile-hero-text">
              <h2 className="mobile-hero-title">{slideContent.title}</h2>
              <p className="mobile-hero-desc">{slideContent.desc}</p>
            </div>
        </div>
      </section>


      {/* --- NEW DROPS Mobile Section --- */}
      <section style={{
        padding: '60px 0',
        background: '#fff'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 20px'
        }}>
          
          <h2 style={{
            fontSize: '28px',
            fontWeight: '600',
            color: '#333',
            margin: '0 0 40px 0',
            fontFamily: '"Inter", sans-serif',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            lineHeight: '1.4',
            textAlign: 'center'
          }}>NEW DROPS</h2>
          
          {/* Enhanced Product Grid */}
          <div style={{
            display: 'flex',
            overflowX: 'auto',
            gap: '16px',
            paddingBottom: '20px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitScrollbar: { display: 'none' }
          }}>
            {newDropsLoading ? (
              <ProductSkeleton count={4} className="mobile-skeleton" />
            ) : newDrops && newDrops.length > 0 ? (
              newDrops.slice(0, 4).map((product) => (
                <Link 
                  key={product.id} 
                  to={`/catalogue#${product.id}`} 
                  style={{
                    textDecoration: 'none',
                    color: 'inherit',
                    display: 'block',
                    flexShrink: 0,
                    width: '220px'
                  }}
                >
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '16px',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    height: '320px',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid rgba(0,0,0,0.04)',
                    position: 'relative'
                  }}
                  onTouchStart={(e) => {
                    e.currentTarget.style.transform = 'scale(0.98)';
                  }}
                  onTouchEnd={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  >
                    {/* NEW Badge */}
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      background: '#000000',
                      color: 'white',
                      fontSize: '10px',
                      fontWeight: '600',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      zIndex: 2,
                      fontFamily: '"Inter", sans-serif'
                    }}>
                      NEW
                    </div>
                    
                    {/* Product Image */}
                    <div style={{
                      width: '100%',
                      height: '180px',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#ffffff',
                      position: 'relative'
                    }}>
                      <img 
                        src={product.image} 
                        alt={product.name}
                        style={{
                          width: '65%',
                          height: '65%',
                          objectFit: 'contain',
                          transition: 'transform 0.3s ease'
                        }}
                        loading="lazy" 
                      />
                    </div>
                    
                    {/* Product Info */}
                    <div style={{
                      padding: '20px 16px 16px 16px',
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      <div>
                        <h3 style={{
                          margin: '0 0 8px 0',
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#2c2c2c',
                          fontFamily: '"Inter", sans-serif',
                          lineHeight: '1.3',
                          letterSpacing: '0.3px',
                          textTransform: 'capitalize'
                        }}>{product.name}</h3>
                        
                        <p style={{
                          margin: '0 0 16px 0',
                          fontSize: '13px',
                          color: '#666',
                          fontFamily: '"Inter", sans-serif',
                          lineHeight: '1.4',
                          fontWeight: '500'
                        }}>{product.origin || 'Premium Coffee'}</p>
                      </div>
                      
                      <div style={{
                        fontSize: '18px',
                        fontWeight: '700',
                        color: '#2c2c2c',
                        fontFamily: '"Inter", sans-serif'
                      }}>{product.price} <span style={{ fontSize: '14px', color: '#666' }}>QAR</span></div>
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <div style={{
                width: '100%',
                padding: '60px 20px',
                textAlign: 'center',
                background: 'white',
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
              }}>
                <div style={{
                  fontSize: '48px',
                  marginBottom: '16px'
                }}>☕</div>
                <p style={{
                  fontSize: '16px',
                  color: '#666',
                  fontFamily: '"Inter", sans-serif',
                  margin: '0 0 8px 0'
                }}>
                  {newDropsError ? 'Unable to load products' : 'No new drops available'}
                </p>
                <p style={{
                  fontSize: '14px',
                  color: '#999',
                  fontFamily: '"Inter", sans-serif',
                  margin: '0'
                }}>
                  Check back soon for fresh arrivals
                </p>
              </div>
            )}
          </div>
          
          {/* Show All Button */}
          {newDrops && newDrops.length > 4 && (
            <div style={{
              textAlign: 'center',
              marginTop: '40px'
            }}>
              <Link 
                to="/catalogue"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: '#000000',
                  color: 'white',
                  padding: '14px 28px',
                  borderRadius: '30px',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: '"Inter", sans-serif',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
                  transition: 'all 0.3s ease'
                }}
              >
                VIEW ALL PRODUCTS
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12h14m-7-7l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* --- BRANCH LOCATIONS Mobile Section --- */}
      <section style={{
        padding: '60px 0',
        background: '#fff'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 20px'
        }}>
          <h2 style={{
            fontSize: '28px',
            fontWeight: '600',
            color: '#333',
            margin: '0 0 40px 0',
            fontFamily: '"Inter", sans-serif',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            lineHeight: '1.4',
            textAlign: 'center'
          }}>BRANCH LOCATION</h2>
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0',
            alignItems: 'center'
          }}>
            {branches.map((branch, index) => (
              <div 
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '16px 0',
                  cursor: 'pointer'
                }}
                onClick={() => setSelectedBranch(index)}
              >
                <span style={{
                  fontSize: '20px',
                  fontWeight: selectedBranch === index ? '600' : '400',
                  color: selectedBranch === index ? '#333' : '#999',
                  fontFamily: '"Inter", sans-serif',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>
                  {branch.name}
                </span>
              </div>
            ))}
          </div>
          
          {/* Mobile Branch Details */}
          <div style={{
            marginTop: '40px',
            textAlign: 'center'
          }}>
            {branchesLoading ? (
              <div style={{
                width: '100%',
                aspectRatio: '3/2',
                marginBottom: '24px',
                background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                backgroundSize: '400% 100%',
                animation: 'shimmer 1.5s infinite',
                borderRadius: '0px'
              }} />
            ) : (
              <div style={{
                width: '100%',
                aspectRatio: '3/2',
                marginBottom: '24px',
                overflow: 'hidden',
                backgroundColor: '#f8f8f8',
                borderRadius: '0px'
              }}>
                <img 
                  src={branches[Math.min(selectedBranch, branches.length - 1)]?.imageUrl || '/images/grey-bg.jpg'} 
                  alt={branches[Math.min(selectedBranch, branches.length - 1)]?.name || 'Branch'}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
            )}
            
            {branchesLoading ? (
              <>
                {/* Branch name skeleton */}
                <div style={{
                  height: '24px',
                  width: '60%',
                  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                  backgroundSize: '400% 100%',
                  animation: 'shimmer 1.5s infinite',
                  borderRadius: '4px',
                  marginBottom: '12px',
                  margin: '0 auto 12px auto'
                }} />
                
                {/* Address skeleton */}
                <div style={{
                  height: '16px',
                  width: '80%',
                  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                  backgroundSize: '400% 100%',
                  animation: 'shimmer 1.5s infinite',
                  borderRadius: '4px',
                  marginBottom: '8px',
                  margin: '0 auto 8px auto'
                }} />
                
                {/* Today hours skeleton */}
                <div style={{
                  height: '14px',
                  width: '50%',
                  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                  backgroundSize: '400% 100%',
                  animation: 'shimmer 1.5s infinite',
                  borderRadius: '4px',
                  margin: '0 auto'
                }} />
              </>
            ) : (
              <>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#333',
                  margin: '0 0 12px 0',
                  fontFamily: '"Inter", sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  {branches[Math.min(selectedBranch, branches.length - 1)]?.name || 'Branch'}
                </h3>
                
                <p style={{
                  fontSize: '14px',
                  color: '#666',
                  margin: '0 0 8px 0',
                  fontFamily: '"Inter", sans-serif',
                  lineHeight: '1.6'
                }}>
                  {branches[Math.min(selectedBranch, branches.length - 1)]?.address || 'Address not available'}
                </p>
                
                <div style={{
                  fontSize: '13px',
                  color: '#333',
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: '500'
                }}>
                  <span style={{ color: '#666' }}>Today: </span>
                  {branches[Math.min(selectedBranch, branches.length - 1)]?.openingHours ? 
                (() => {
                  const today = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][new Date().getDay()];
                  const todayHours = branches[Math.min(selectedBranch, branches.length - 1)].openingHours[today];
                  if (todayHours?.closed) {
                    return 'Closed';
                  } else if (todayHours?.open && todayHours?.close) {
                    const formatTime = (time) => {
                      if (!time) return '';
                      const [hours, minutes] = time.split(':');
                      const hour24 = parseInt(hours);
                      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
                      const ampm = hour24 >= 12 ? 'PM' : 'AM';
                      return `${hour12}:${minutes} ${ampm}`;
                    };
                    const openTime = formatTime(todayHours.open);
                    const closeTime = todayHours.close === '24:00' ? '12:00 AM (Next Day)' : formatTime(todayHours.close);
                    return `${openTime} - ${closeTime}`;
                  }
                  return 'Contact for hours';
                })()
                : 'Contact for hours'
              }
                </div>
                
                {/* Visit Us Button */}
                <div style={{
                  marginTop: '24px'
                }}>
                  <a 
                    href={branches[Math.min(selectedBranch, branches.length - 1)]?.googleMapsLink || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(branches[Math.min(selectedBranch, branches.length - 1)]?.address || '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: '#000000',
                      color: 'white',
                      padding: '12px 24px',
                      borderRadius: '30px',
                      textDecoration: 'none',
                      fontSize: '14px',
                      fontWeight: '600',
                      fontFamily: '"Inter", sans-serif',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    VISIT US
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

    </div>
  );
};

const DesktopHomepage = ({ slides, bestSellers, branches, branchesLoading, fading, goToSlide, currentSlide, bestSellersLoading, bestSellersError, newDrops, newDropsLoading, newDropsError, setSelectedBranch, selectedBranch, CoffeeBeanIndicator, movingBeanIndex, desktopExpandedBranch, setDesktopExpandedBranch, setHeroImageLoaded, desktopBranchImageLoaded, setDesktopBranchImageLoaded, isMobile }) => {
  const [expandedBranches, setExpandedBranches] = useState(new Set());
  

  const toggleBranchExpansion = (index) => {
    const newExpanded = new Set(expandedBranches);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedBranches(newExpanded);
  };

  const getFullHours = (branch) => {
    // Use Firebase opening hours if available
    if (branch.openingHours) {
      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      
      return days.map((day, index) => {
        const dayInfo = branch.openingHours[day];
        if (dayInfo?.closed) {
          return { day: dayNames[index], hours: 'Closed' };
        }
        if (dayInfo?.open && dayInfo?.close) {
          const openTime = formatTime(dayInfo.open);
          const closeTime = dayInfo.close === '24:00' ? '12:00 AM (Next Day)' : formatTime(dayInfo.close);
          return { day: dayNames[index], hours: `${openTime} - ${closeTime}` };
        }
        return { day: dayNames[index], hours: 'Contact for hours' };
      });
    }

    // Fallback to hardcoded logic if no Firebase data
    const name = branch.name?.toLowerCase() || '';
    if (name.includes('al wakrah') || name.includes('wakra')) {
      return [
        { day: 'Monday', hours: '5:00 AM - 11:00 PM' },
        { day: 'Tuesday', hours: '5:00 AM - 11:00 PM' },
        { day: 'Wednesday', hours: '5:00 AM - 11:00 PM' },
        { day: 'Thursday', hours: '5:00 AM - 12:00 AM' },
        { day: 'Friday', hours: '7:00 AM - 12:00 AM' },
        { day: 'Saturday', hours: '7:00 AM - 11:00 PM' },
        { day: 'Sunday', hours: '5:00 AM - 11:00 PM' }
      ];
    } else if (name.includes('gewan')) {
      return [
        { day: 'Monday', hours: '9:00 AM - 11:00 PM' },
        { day: 'Tuesday', hours: '9:00 AM - 11:00 PM' },
        { day: 'Wednesday', hours: '9:00 AM - 11:00 PM' },
        { day: 'Thursday', hours: '9:00 AM - 11:00 PM' },
        { day: 'Friday', hours: '9:00 AM - 11:00 PM' },
        { day: 'Saturday', hours: '9:00 AM - 11:00 PM' },
        { day: 'Sunday', hours: '9:00 AM - 11:00 PM' }
      ];
    } else if (name.includes('popup')) {
      return [
        { day: 'Contact', hours: branch.contactInfo || '+974 5000 8852' }
      ];
    }
    return [
      { day: 'Contact', hours: 'Call for hours' }
    ];
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour24 = parseInt(hours);
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes} ${ampm}`;
  };

  return (
    <>
      <div className="hero-section">
        <div className="locked-ratio-frame">
          <section className="grey-hero-figma">
            <div className="grey-hero-figma-highlight" />
            <div className="grey-hero-figma-content">
              <div className={`grey-hero-figma-left${fading ? ' fading' : ''}`}>
                <h2 className="grey-hero-figma-heading">{slides[currentSlide].title}</h2>
                <p className="grey-hero-figma-desc">{slides[currentSlide].desc}</p>
              </div>
              <div className="grey-hero-figma-right">
                <img
                  src={slides[currentSlide].image}
                  alt={slides[currentSlide].title}
                  className={`grey-hero-figma-image${fading ? ' fading' : ''}`}
                  style={slides[currentSlide].imageStyle}
                  onLoad={() => setHeroImageLoaded(true)}
                />
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* NEW DROPS SECTION - Modern Design */}
      <section className="" style={{
        padding: isMobile ? '10px 0 60px 0' : '0px 0 80px 0',
        background: '#fff'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: isMobile ? '0 20px' : '0 40px',
          marginTop: isMobile ? '0' : '-150px'
        }}>
          
          {/* Header Section - Left Aligned */}
          <div style={{
            textAlign: isMobile ? 'center' : 'left',
            marginBottom: isMobile ? '20px' : '10px'
          }}>
            <h2 style={{
              fontSize: isMobile ? '24px' : '32px',
              fontWeight: '500',
              color: '#333',
              margin: '0',
              fontFamily: '"Inter", sans-serif',
              letterSpacing: '0.02em',
              textTransform: 'none',
              lineHeight: '1.2'
            }}>NEW DROPS</h2>
          </div>
          
          {/* Mobile Horizontal Scroll Design */}
          {isMobile ? (
            <div style={{
              overflowX: 'auto',
              paddingBottom: '20px',
              marginBottom: '20px'
            }}>
              <div style={{
                display: 'flex',
                gap: '16px',
                width: 'max-content',
                paddingRight: '20px'
              }}>
                {newDropsLoading ? (
                  <ProductSkeleton count={4} className="desktop-skeleton" />
                ) : newDrops.length > 0 ? (
                  newDrops.map((product, index) => (
                    <Link key={product.id} to={`/catalogue#${product.id}`} className="product-card" style={{
                      textDecoration: 'none',
                      color: 'inherit',
                      flexShrink: 0
                    }}>
                      <div style={{
                        width: '160px',
                        background: '#fff',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        transition: 'all 0.3s ease',
                        border: '1px solid #f0f0f0'
                      }}>
                        <div style={{
                          width: '100%',
                          height: '160px',
                          overflow: 'hidden',
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#f8f8f8'
                        }}>
                          <img 
                            src={product.image} 
                            alt={product.name}
                            style={{
                              width: '85%',
                              height: '85%',
                              objectFit: 'contain'
                            }}
                            loading="lazy" 
                          />
                          <div style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            background: 'rgba(0,0,0,0.7)',
                            color: '#fff',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '10px',
                            fontWeight: '600',
                            fontFamily: '"Inter", sans-serif'
                          }}>NEW</div>
                        </div>
                        
                        <div style={{
                          padding: '16px 12px',
                          textAlign: 'center'
                        }}>
                          <h3 style={{
                            margin: '0 0 6px 0',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#222',
                            fontFamily: '"Inter", sans-serif',
                            lineHeight: '1.3',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>{product.name}</h3>
                          <p style={{
                            margin: '0 0 8px 0',
                            fontSize: '11px',
                            color: '#666',
                            fontFamily: '"Inter", sans-serif',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>{product.origin || 'Premium'}</p>
                        <span style={{
                          fontSize: '15px',
                          fontWeight: '700',
                          color: '#222',
                          fontFamily: '"Inter", sans-serif'
                        }}>{product.price} QAR</span>
                        </div>
                      </div>
                    </Link>
                  ))
                ) : (
                  <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    color: '#666',
                    width: '200px'
                  }}>
                    <p style={{
                      fontSize: '14px',
                      fontStyle: 'italic',
                      fontFamily: '"Inter", sans-serif'
                    }}>Coming soon...</p>
                  </div>
                )}
              </div>
              
            </div>
          ) : (
            /* Desktop Grid */
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '30px',
              justifyItems: 'stretch'
            }}>
              {newDropsLoading ? (
                <ProductSkeleton count={4} className="grid-skeleton" />
              ) : newDrops.length > 0 ? (
                newDrops.slice(0, 4).map((product, index) => (
                  <Link 
                    key={product.id} 
                    to={`/catalogue#${product.id}`} 
                    style={{
                      textDecoration: 'none',
                      color: 'inherit',
                      display: 'block'
                    }}
                  >
                    <div style={{
                      backgroundColor: 'white',
                      borderRadius: '0px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      overflow: 'hidden',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      cursor: 'pointer',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                    }}
                    >
                      {/* Product Image */}
                      <div style={{
                        width: '100%',
                        aspectRatio: '1/1',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f8f8f8'
                      }}>
                        <img 
                          src={product.image} 
                          alt={product.name}
                          style={{
                            width: '85%',
                            height: '85%',
                            objectFit: 'contain'
                          }}
                          loading="lazy" 
                        />
                      </div>
                      
                      {/* Product Info */}
                      <div style={{
                        padding: '20px',
                        borderTop: '1px solid #e0e0e0',
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        textAlign: 'left'
                      }}>
                        <div>
                          <h3 style={{
                            margin: '0 0 8px 0',
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#222',
                            fontFamily: '"Inter", sans-serif',
                            lineHeight: '1.3',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>{product.name}</h3>
                          
                          <p style={{
                            margin: '0 0 16px 0',
                            fontSize: '14px',
                            color: '#666',
                            fontFamily: '"Inter", sans-serif',
                            lineHeight: '1.4'
                          }}>{product.origin || 'Premium Coffee'}</p>
                        </div>
                        
                        <div style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#222',
                          fontFamily: '"Inter", sans-serif'
                        }}>{product.price} QAR</div>
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div style={{
                  gridColumn: '1 / -1',
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: '#666'
                }}>
                  <p style={{
                    fontSize: '16px',
                    fontStyle: 'italic',
                    fontFamily: 'Inter, Arial, sans-serif'
                  }}>New drops coming soon...</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* BRANCH LOCATIONS SECTION - Exact Screenshot Recreation */}
      <section style={{
        padding: '80px 0',
        background: '#fff'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 40px'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '280px 1fr',
            gap: '60px',
            minHeight: '400px',
            alignItems: 'start'
          }}>
            {/* Left Sidebar - Branch Navigation */}
            <div style={{
              borderRight: '1px solid #000',
              paddingRight: '40px',
              minHeight: '700px'
            }}>
              <h2 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#333',
                margin: '0 0 8px 0',
                fontFamily: '"Inter", sans-serif',
                letterSpacing: '2px',
                textTransform: 'uppercase',
                lineHeight: '1.4'
              }}>BRANCH<br/>LOCATION</h2>
              
              <div style={{
                width: 'calc(100% + 40px)',
                height: '1px',
                backgroundColor: '#000',
                marginBottom: '15px',
                marginRight: '-40px'
              }}></div>
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0',
                paddingLeft: '20px'
              }}>
                {branchesLoading ? (
                  // Branch loading skeleton
                  Array.from({ length: 3 }, (_, index) => (
                    <div key={index} style={{
                      paddingTop: index === 0 ? '18px' : '24px',
                      paddingBottom: '0px',
                      borderBottom: '1px solid #000',
                      marginRight: '-40px'
                    }}>
                      <div style={{
                        width: '120px',
                        height: '16px',
                        backgroundColor: '#f0f0f0',
                        borderRadius: '4px',
                        animation: 'shimmer 1.5s infinite'
                      }}></div>
                    </div>
                  ))
                ) : branches.map((branch, index) => (
                  <div 
                    key={index}
                    style={{
                      paddingTop: index === 0 ? '18px' : '24px',
                      paddingBottom: '0px',
                      borderBottom: '1px solid #000',
                      cursor: 'pointer',
                      marginRight: '-40px'
                    }}
                    onClick={() => setSelectedBranch(index)}
                  >
                    <span style={{
                      fontSize: '20px',
                      fontWeight: selectedBranch === index ? '600' : '400',
                      color: selectedBranch === index ? '#333' : '#999',
                      fontFamily: '"Inter", sans-serif',
                      letterSpacing: '1px',
                      textTransform: 'uppercase'
                    }}>
                      {branch.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Content Area - Branch Details */}
            <div style={{
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Branch Image */}
              <div style={{
                width: '100%',
                aspectRatio: '3/2',
                marginBottom: '30px',
                overflow: 'hidden',
                backgroundColor: '#f8f8f8',
                borderRadius: '0px'
              }}>
                <img 
                  src={branches[selectedBranch]?.imageUrl || '/images/grey-bg.jpg'} 
                  alt={branches[selectedBranch]?.name || 'Branch'}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
              
              {/* Branch Info */}
              <div style={{
                textAlign: 'center'
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#333',
                  margin: '0 0 12px 0',
                  fontFamily: '"Inter", sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  {branches[Math.min(selectedBranch, branches.length - 1)]?.name || 'Branch'}
                </h3>
                
                <p style={{
                  fontSize: '14px',
                  color: '#666',
                  margin: '0 0 8px 0',
                  fontFamily: '"Inter", sans-serif',
                  lineHeight: '1.6'
                }}>
                  {branches[Math.min(selectedBranch, branches.length - 1)]?.address || 'Address not available'}
                </p>
                
                <div style={{
                  fontSize: '13px',
                  color: '#333',
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: '500'
                }}>
                  <span style={{ color: '#666' }}>Today: </span>
                  {branches[Math.min(selectedBranch, branches.length - 1)]?.openingHours ? 
                    (() => {
                      const today = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][new Date().getDay()];
                      const todayHours = branches[Math.min(selectedBranch, branches.length - 1)].openingHours[today];
                      if (todayHours?.closed) {
                        return 'Closed';
                      } else if (todayHours?.open && todayHours?.close) {
                        const formatTime = (time) => {
                          if (!time) return '';
                          const [hours, minutes] = time.split(':');
                          const hour24 = parseInt(hours);
                          const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
                          const ampm = hour24 >= 12 ? 'PM' : 'AM';
                          return `${hour12}:${minutes} ${ampm}`;
                        };
                        const openTime = formatTime(todayHours.open);
                        const closeTime = todayHours.close === '24:00' ? '12:00 AM (Next Day)' : formatTime(todayHours.close);
                        return `${openTime} - ${closeTime}`;
                      }
                      return 'Contact for hours';
                    })()
                    : 'Contact for hours'
                  }
                </div>
                
                {/* Visit Us Button */}
                <div style={{
                  marginTop: '24px'
                }}>
                  <a 
                    href={branches[Math.min(selectedBranch, branches.length - 1)]?.googleMapsLink || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(branches[Math.min(selectedBranch, branches.length - 1)]?.address || '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: '#000000',
                      color: 'white',
                      padding: '12px 24px',
                      borderRadius: '30px',
                      textDecoration: 'none',
                      fontSize: '14px',
                      fontWeight: '600',
                      fontFamily: '"Inter", sans-serif',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    VISIT US
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </>
  );
};

// --- HELPER FUNCTION FOR BRANCH STATUS ---
const getBranchStatus = (branchName) => {
  const now = new Date();
  const qatarTime = new Date(now.toLocaleString("en-US", {timeZone: "Asia/Qatar"}));
  const currentHour = qatarTime.getHours();
  const currentMinute = qatarTime.getMinutes();
  const currentTime = currentHour * 60 + currentMinute; // Convert to minutes for easier comparison
  const currentDay = qatarTime.getDay(); // 0 = Sunday, 1 = Monday, etc.

  const name = branchName.toLowerCase();
  
  if (name.includes('al wakrah') || name.includes('wakra')) {
    // Al Wakrah hours: Mon-Wed: 5AM-11PM, Thu: 5AM-12AM, Fri: 7AM-12AM, Sat: 7AM-11PM, Sun: 5AM-11PM
    if (currentDay >= 1 && currentDay <= 3) { // Mon-Wed
      return currentTime >= 5*60 && currentTime < 23*60 ? 'OPEN' : 'CLOSED';
    } else if (currentDay === 4) { // Thursday
      return currentTime >= 5*60 ? 'OPEN' : 'CLOSED'; // 5AM-12AM (next day)
    } else if (currentDay === 5) { // Friday
      return currentTime >= 7*60 ? 'OPEN' : 'CLOSED'; // 7AM-12AM (next day)
    } else if (currentDay === 6) { // Saturday
      return currentTime >= 7*60 && currentTime < 23*60 ? 'OPEN' : 'CLOSED';
    } else { // Sunday
      return currentTime >= 5*60 && currentTime < 23*60 ? 'OPEN' : 'CLOSED';
    }
  } else if (name.includes('gewan')) {
    // Gewan: Daily 9AM-11PM
    return currentTime >= 9*60 && currentTime < 23*60 ? 'OPEN' : 'CLOSED';
  } else if (name.includes('popup')) {
    // Popup store - always show as available for contact
    return 'CONTACT';
  }
  
  return 'OPEN'; // Default fallback
};

// Promo Banner component (reused)
const PromoBanner = () => (
  <div style={{
    background: '#f8f8f8',
    borderBottom: '1px solid #e8e8e8',
    padding: '12px 24px',
    position: 'relative'
  }}>
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '16px'
    }}>
      <div style={{
        width: '40px',
        height: '1px',
        background: '#ddd'
      }} />
      <span style={{
        fontSize: '13px',
        fontWeight: '500',
        color: '#333',
        textTransform: 'uppercase',
        letterSpacing: '1.5px',
        fontFamily: 'inherit'
      }}>
        Free Delivery on Orders Above QAR 100
      </span>
      <div style={{
        width: '40px',
        height: '1px',
        background: '#ddd'
      }} />
    </div>
  </div>
);


// --- MAIN HOMEPAGE COMPONENT ---
const HomePage = () => {
  // --- STATE ---
  // Current hero slide index
  const [currentSlide, setCurrentSlide] = useState(0);
  const [fading, setFading] = useState(false); // For fade animation on slide change
  const [movingBeanIndex, setMovingBeanIndex] = useState(null); // For sliding bean animation
  const [drops, setDrops] = useState([]);
  const [dropsLoading, setDropsLoading] = useState(true);
  const [dropsError, setDropsError] = useState('');
  const isInitialMount = useRef(true);
  const [heroImageLoaded, setHeroImageLoaded] = useState(false);
  const [desktopBranchImageLoaded, setDesktopBranchImageLoaded] = useState(true); // Start as true
  const isBranchInitialMount = useRef(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  // --- BRANCH LOCATION STATE ---
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(true);
  const [selectedBranch, setSelectedBranch] = useState(0);
  const [mobileExpandedBranch, setMobileExpandedBranch] = useState(null);
  const [desktopExpandedBranch, setDesktopExpandedBranch] = useState(0); // Start with the first one open

  const { addToCart } = useCart();
  const { currentUser } = useAuth();
  const [cartMessage, setCartMessage] = useState(null);
  const navigate = useNavigate();
  

  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 1024 : false
  );
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- TRACK PAGE VIEW ---
  useEffect(() => {
    // Track page view for analytics
    analytics.trackPageView(window.location.pathname);
  }, []);

  // --- UPDATE TIME EVERY MINUTE ---
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute
    return () => clearInterval(timer);
  }, []);

  // --- SLIDES DATA ---
  // Each slide can have custom text, button, and (optionally) image or style overrides
  const slides = useMemo(() => ([
    {
      title: 'Get to Know Us',
      desc: `We're GREY Specialty Coffee & Roastery as Qatar's for most destination and trusted supplier for specialty coffee, distinguished by an unwavering commitment to quality, innovation, and cultural authenticity ...`,
      button: 'Read More',
      image: '/images/barista.png',
      imageStyle: {
        objectPosition: 'center',
        marginTop: '45px',
        marginRight: '-70px',
      },
    },
    {
      title: 'Back to List',
      desc: `No gear? No problem. Craving great coffee on the go — whether you're traveling, at work, or taking a break? Our Coffee Drip Bags are back, so you can brew specialty coffee anytime, anywhere, no machine needed. Grab yours now and explore our beans.`,
      button: 'Read more',
      image: '/images/dripbag.png',
      imageStyle: {
        width: '570px',
        height: 'auto',
        objectFit: 'contain',
        marginRight: '-10px',
        marginTop: '163px',
        background: 'none',
      },
    },
    {
      title: 'Cool Down with GREY',
      desc: `Beat the heat with our refreshing ice cream collection. From classic vanilla to innovative coffee-inspired flavors, our premium ice cream is the perfect way to enjoy GREY's quality in a cool, creamy treat.`,
      button: 'Explore flavors',
      image: '/images/icecream.png',
      imageStyle: {
        width: '520px',
        height: 'auto',
        objectFit: 'contain',
        objectPosition: 'bottom',
        marginTop: '230px',
        marginRight: '-70px',
        background: 'none',
      },
    },
  ]), []);

  // --- Preload Images ---
  useEffect(() => {
    slides.forEach(slide => {
      const img = new Image();
      img.src = slide.image;
    });
  }, [slides]);

  // --- Preload Branch Images ---
  useEffect(() => {
    if (branches.length > 0) {
      branches.forEach(branch => {
        if (branch.imageUrl) {
          const img = new Image();
          img.src = branch.imageUrl;
        }
      });
    }
  }, [branches]);

  // --- SLIDE TRANSITION LOGIC ---
  // Handles fade and bean animation when changing slides
  const goToSlide = (i) => {
    if (i === currentSlide || fading) return;
    
    setFading(true);

    if (isMobile) {
      setTimeout(() => setFading(false), 700); // Mobile animation lock release
    } else {
      setHeroImageLoaded(false); // Desktop lock is released by onLoad
    }

    setTimeout(() => setCurrentSlide(i), 600); // Swap content after fade-out
  };

  // --- AUTO SLIDESHOW ---
  useEffect(() => {
    const interval = setInterval(() => {
      if (fading) return;
      
        const next = (currentSlide + 1) % slides.length;
        setFading(true);
      
        if (isMobile) {
            setTimeout(() => setFading(false), 700); // Mobile animation lock release
        } else {
            setHeroImageLoaded(false); // Desktop lock is released by onLoad
        }

        setTimeout(() => setCurrentSlide(next), 600);
      
    }, 5000); // 5 seconds
    return () => clearInterval(interval);
  }, [currentSlide, fading, slides.length, isMobile]);

  // This effect handles the FADE IN of the new slide
  useEffect(() => {
    if (isInitialMount.current) {
      // On first load, we don't need to fade in, just show it.
      setHeroImageLoaded(true); 
      isInitialMount.current = false;
      return;
    }
    
    if (heroImageLoaded) {
      // When the new image is loaded, trigger the fade-in.
      setFading(false);
    }
  }, [heroImageLoaded]);

  // This effect resets the branch image loaded state on change
  useEffect(() => {
    if (isBranchInitialMount.current) {
      isBranchInitialMount.current = false;
      return;
    }
    // When a new branch is selected, reset the loaded state
    setDesktopBranchImageLoaded(false);
  }, [selectedBranch]);

  // --- RENDER ---
  useEffect(() => {
    const fetchDrops = async () => {
      setDropsLoading(true);
      setDropsError('');
      try {
        const docRef = doc(db, 'homepage', 'branchLocations');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists() && Array.isArray(docSnap.data().newDrops)) {
          setDrops(docSnap.data().newDrops);
        } else {
          setDrops([]);
        }
      } catch (err) {
        setDropsError('Failed to load new drops.');
      }
      setDropsLoading(false);
    };
    fetchDrops();
  }, []);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        setBranchesLoading(true);
        console.log('🔥 FIREBASE DEBUG: Fetching branches from Firebase...');
        const docRef = doc(db, 'homepage', 'branchLocations');
        const docSnap = await getDoc(docRef);
        console.log('Firebase docSnap exists:', docSnap.exists());
        if (docSnap.exists()) {
          console.log('Firebase data:', docSnap.data());
          if (Array.isArray(docSnap.data().branches)) {
            console.log('Branches array found:', docSnap.data().branches);
            setBranches(docSnap.data().branches);
            // Reset selectedBranch if it's out of bounds
            if (selectedBranch >= docSnap.data().branches.length) {
              setSelectedBranch(0);
            }
          } else {
            console.log('Branches data is not an array or missing');
            setBranches([]);
            setSelectedBranch(0);
          }
        } else {
          console.log('Firebase document does not exist');
          setBranches([]);
          setSelectedBranch(0);
        }
      } catch (err) {
        console.error('Error fetching branches from Firebase:', err);
        setBranches([]);
        setSelectedBranch(0);
      }
      setBranchesLoading(false);
    };
    fetchBranches();
  }, []);

  const [bestSellers, setBestSellers] = useState([]);
  const [bestSellersLoading, setBestSellersLoading] = useState(true);
  const [bestSellersError, setBestSellersError] = useState('');
  const [newDrops, setNewDrops] = useState([]);
  const [newDropsLoading, setNewDropsLoading] = useState(true);
  const [newDropsError, setNewDropsError] = useState('');

  useEffect(() => {
    const fetchBestSellers = async () => {
      setBestSellersLoading(true);
      setBestSellersError('');
      try {
        const q = query(collection(db, 'products'), where('bestSeller', '==', true));
        const snap = await getDocs(q);
        const list = [];
        snap.forEach(docSnap => list.push({ id: docSnap.id, ...docSnap.data() }));
        setBestSellers(list);
      } catch (err) {
        setBestSellersError('Failed to load best sellers.');
      }
      setBestSellersLoading(false);
    };
    fetchBestSellers();
  }, []);

  useEffect(() => {
    const fetchNewDrops = async () => {
      setNewDropsLoading(true);
      setNewDropsError('');
      try {
        const q = query(collection(db, 'products'), where('isNewDrop', '==', true), where('active', '==', true));
        const snap = await getDocs(q);
        const list = [];
        snap.forEach(docSnap => list.push({ id: docSnap.id, ...docSnap.data() }));
        
        // If no new drops found, fall back to recent products for demo
        if (list.length === 0) {
          const fallbackQuery = query(collection(db, 'products'), where('active', '==', true));
          const fallbackSnap = await getDocs(fallbackQuery);
          const fallbackList = [];
          fallbackSnap.forEach(docSnap => fallbackList.push({ id: docSnap.id, ...docSnap.data() }));
          setNewDrops(fallbackList.slice(0, 4)); // Show first 4 products as demo
        } else {
          setNewDrops(list);
        }
        console.log('NewDrops loaded:', list.length > 0 ? list : 'Empty list');
      } catch (err) {
        console.error('NewDrops error:', err);
        setNewDropsError('Failed to load new drops.');
      }
      setNewDropsLoading(false);
    };
    fetchNewDrops();
  }, []);

  return (
    <div className="grey-bg-background">
      <Header />
      <div className="homepage">
        <div className="full-viewport-background"></div>
        <div className="homepage-content">
          {isMobile ? (
            <MobileHomepage 
              slides={slides} 
              bestSellers={bestSellers}
              branches={branches}
              branchesLoading={branchesLoading}
              goToSlide={goToSlide}
              currentSlide={currentSlide}
              bestSellersLoading={bestSellersLoading}
              bestSellersError={bestSellersError}
              newDrops={newDrops}
              newDropsLoading={newDropsLoading}
              newDropsError={newDropsError}
              setSelectedBranch={setSelectedBranch}
              setMobileExpandedBranch={setMobileExpandedBranch}
              selectedBranch={selectedBranch}
              mobileExpandedBranch={mobileExpandedBranch}
              CoffeeBeanIndicator={CoffeeBeanIndicator}
              isMobile={isMobile}
            />
          ) : (
            <DesktopHomepage 
              slides={slides}
              bestSellers={bestSellers}
              branches={branches}
              branchesLoading={branchesLoading}
              fading={fading}
              goToSlide={goToSlide}
              currentSlide={currentSlide}
              bestSellersLoading={bestSellersLoading}
              bestSellersError={bestSellersError}
              newDrops={newDrops}
              newDropsLoading={newDropsLoading}
              newDropsError={newDropsError}
              setSelectedBranch={setSelectedBranch}
              selectedBranch={selectedBranch}
              CoffeeBeanIndicator={CoffeeBeanIndicator}
              movingBeanIndex={movingBeanIndex}
              desktopExpandedBranch={desktopExpandedBranch}
              setDesktopExpandedBranch={setDesktopExpandedBranch}
              setHeroImageLoaded={setHeroImageLoaded}
              desktopBranchImageLoaded={desktopBranchImageLoaded}
              setDesktopBranchImageLoaded={setDesktopBranchImageLoaded}
              isMobile={isMobile}
            />
          )}
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default HomePage;
// ---------------------------------------------
// End GREY Homepage (Pixel-Perfect Figma Match)
// ---------------------------------------------
